import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../images/logo.png';
import '../../styles/Navbar.css';
import { AuthContext } from '../../context/AuthContext';
import ProfileAvatar from '../ProfileAvatar';

/* ─── Navigation structure ─────────────────────────────────────── */
const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  {
    label: 'About Us',
    key: 'about',
    links: [
      { label: 'About Sanstha', to: '/about-sanstha' },
      { label: 'About College', to: '/about-college' },
      { label: 'Vision & Mission', to: '/vision-mission' },
      { label: 'About Us (Overview)', to: '/about-us' },
    ],
  },
  {
    label: 'Management',
    key: 'management',
    links: [
      { label: 'Governing Council', to: '/governing-council' },
      { label: 'College Development Committee', to: '/college-development-committee' },
    ],
  },
  {
    label: 'Academics',
    key: 'academics',
    links: [
      { label: 'UG Courses', to: '/ugcourses' },
      { label: 'Certificate Courses', to: '/certificate-courses' },
      { label: 'Library', to: '/library' },
      { label: 'Physical Education', to: '/physical-education' },
      { label: 'Programme Outcome', to: '/programme-outcome' },
      { label: 'Environment Awareness', to: '/environment-awareness' },
    ],
  },
  {
    label: 'Departments',
    key: 'departments',
    links: [
      { label: 'Arts Department', to: '/arts-department' },
      { label: 'Science Department', to: '/science-department' },
      { label: 'Computer Science', to: '/computer-science-department' },
    ],
  },
  {
    label: 'IQAC',
    key: 'iqac',
    links: [
      { label: 'IQAC Committee', to: '/iqac-committee' },
      { label: 'AQAR Reports', to: '/iqac-aqar-reports' },
      { label: 'Meeting Minutes', to: '/iqac-meeting-minutes' },
      { label: 'Self Study Report (SSR)', to: '/iqac-ssr' },
      { label: 'NAAC Certification', to: '/iqac-certification' },
      { label: 'Best Practices', to: '/iqac-best-practices' },
      { label: 'Seminars / Conferences', to: '/iqac-seminars' },
      { label: 'Extension Activity', to: '/iqac-extension-activity' },
      { label: 'Capacity Building', to: '/iqac-capacity-building' },
    ],
  },
  {
    label: 'Admissions',
    key: 'admissions',
    links: [
      { label: 'Admission Process', to: '/admission-process' },
      { label: 'First Year Admission', to: '/first-year-admission' },
      { label: 'Second Year Admission', to: '/second-year-admission' },
      { label: 'Third Year Admission', to: '/third-year-admission' },
      { label: 'Admission Policy', to: '/admission-policy' },
      { label: 'Scholarships', to: '/scholarships' },
    ],
  },
  {
    label: 'Examinations',
    key: 'examinations',
    links: [
      { label: 'Exam Notices', to: '/exam-notices' },
      { label: 'Exam Results', to: '/exam-results' },
      { label: 'Timetables', to: '/exam-timetables' },
      { label: 'Exam Committee', to: '/exam-committee' },
    ],
  },
  {
    label: 'Students Corner',
    key: 'students',
    links: [
      { label: 'NSS', to: '/student-corner-nss' },
      { label: 'Magazine (Utkarsh)', to: '/magazine' },
      { label: 'Alumni', to: '/alumni' },
      { label: 'Academic Calendar', to: '/academic-calendar' },
      { label: 'Old Question Papers', to: '/old-question-papers' },
      { label: 'Study Material', to: '/study-material' },
      { label: 'PowerPoint Bank', to: '/powerpoint-bank' },
      { label: 'Govt. Scholarship', to: '/government-scholarship' },
      { label: 'University Scholarship', to: '/university-scholarship' },
      { label: 'Eligibility Number', to: '/eligibility-number' },
      { label: 'Enrollment List', to: '/enrollment-list' },
      { label: 'Board of Student Welfare', to: '/board-student-development' },
      { label: 'Grievance Cell', to: '/grievance-cell' },
      { label: 'Caste Discrimination Cell', to: '/caste-discrimination' },
      { label: '🎉 Events & Polls', to: '/events' },
    ],
  },
  { label: 'Committees', to: '/committees' },
  {
    label: 'Feedback',
    key: 'feedback',
    links: [
      { label: 'Student Feedback', to: '/feedback/student' },
      { label: 'Teacher Feedback', to: '/feedback/teacher' },
      { label: 'Library Feedback', to: '/feedback/library' },
      { label: 'Alumni Feedback', to: '/feedback/alumni' },
      { label: 'Parents Feedback', to: '/feedback/parents' },
      { label: 'Employer Feedback', to: '/feedback/employer' },
      { label: 'Feedback Report', to: '/feedback/report' },
      { label: 'Action Taken Report', to: '/feedback/action-taken' },
    ],
  },
  {
    label: 'More',
    key: 'more',
    links: [
      { label: 'News & Events', to: '/news-and-events' },
      { label: 'ICT Facilities', to: '/ict-facilities' },
      { label: 'Teacher List', to: '/teacher-list' },
      { label: 'MOU', to: '/mou' },
      { label: 'Linkage', to: '/linkage' },
      { label: 'RTI', to: '/rti' },
      { label: 'SSS', to: '/sss' },
      { label: 'FAQ', to: '/faq' },
      { label: 'Contact Us', to: '/contact-us' },
    ],
  },
];

function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  // Close everything on route change
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'auto';
  }, [menuOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (key) => setActiveDropdown(activeDropdown === key ? null : key);

  return (
    <header className="header" ref={navRef}>
      {/* Top Bar */}
      <div className="top-bar">
        <img src={logo} alt="College Logo" className="college-logo" />
        <h1 className="college-name-navbar">SHRI MULIKADEVI MAHAVIDHYALAY, NIGHOJ</h1>

        {/* Mobile-only auth icon (always visible alongside hamburger) */}
        <div className="top-bar-auth">
          {!user ? (
            <Link to="/login" className="login-btn" onClick={() => setMenuOpen(false)}>Login</Link>
          ) : (
            <div className="profile-dropdown">
              <div className="profile-trigger" onClick={() => setProfileOpen(!profileOpen)}>
                <ProfileAvatar user={user} />
              </div>
              {profileOpen && (
                <div className="profile-menu profile-menu-topbar">
                  <Link to="/dashboard" onClick={() => { setProfileOpen(false); setMenuOpen(false); }}>Overview</Link>
                  <Link to="/profile" onClick={() => { setProfileOpen(false); setMenuOpen(false); }}>My Profile</Link>
                  {['admin', 'superadmin'].includes(user.role) && (
                    <>
                      <Link to="/admin-dashboard" onClick={() => { setProfileOpen(false); setMenuOpen(false); }}>Admin Management</Link>
                      <Link to="/admin/polls" onClick={() => { setProfileOpen(false); setMenuOpen(false); }}>🗳️ Poll Management</Link>
                    </>
                  )}
                  <Link to="/settings" onClick={() => { setProfileOpen(false); setMenuOpen(false); }}>Settings</Link>
                  <button onClick={() => { logout(); setProfileOpen(false); setMenuOpen(false); }}>Logout</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          <div className={`bar ${menuOpen ? 'rotate1' : ''}`} />
          <div className={`bar ${menuOpen ? 'fade' : ''}`} />
          <div className={`bar ${menuOpen ? 'rotate2' : ''}`} />
        </div>
      </div>

      {/* Navbar Links */}
      <nav className="navbar">
        <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
          {NAV_ITEMS.map((item) => {
            if (item.to) {
              // Simple link
              return (
                <Link key={item.to} to={item.to} onClick={() => setMenuOpen(false)}>
                  {item.label}
                </Link>
              );
            }
            // Dropdown
            return (
              <div key={item.key} className="dropdown">
                <span
                  className="dropdown-toggle"
                  onClick={() => toggle(item.key)}
                >
                  {item.label} ▾
                </span>
                <div className={`dropdown-menu ${activeDropdown === item.key ? 'show' : ''}`}>
                  {item.links.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => { setMenuOpen(false); setActiveDropdown(null); }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Auth Section */}
          <div className="nav-auth-section">
            {!user ? (
              <Link to="/login" className="login-btn" onClick={() => setMenuOpen(false)}>
                Login
              </Link>
            ) : (
              <div className="profile-dropdown">
                <div className="profile-trigger" onClick={() => setProfileOpen(!profileOpen)}>
                  <ProfileAvatar user={user} />
                </div>
                {profileOpen && (
                  <div className="profile-menu">
                    <Link to="/dashboard" onClick={() => setProfileOpen(false)}>Overview</Link>
                    <Link to="/profile" onClick={() => setProfileOpen(false)}>My Profile</Link>
                    {['admin', 'superadmin'].includes(user.role) && (
                      <>
                        <Link to="/admin-dashboard" onClick={() => setProfileOpen(false)}>Admin Management</Link>
                        <Link to="/admin/polls" onClick={() => setProfileOpen(false)}>🗳️ Poll Management</Link>
                      </>
                    )}
                    <Link to="/settings" onClick={() => setProfileOpen(false)}>Settings</Link>
                    <button onClick={() => { logout(); setProfileOpen(false); }}>Logout</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
